import React, { useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { motion, useReducedMotion } from 'framer-motion';
import { cn } from '../../../utils/cn';
import { authCashflowData } from '../authData';
import type { AuthVisualVariant } from '../authVisualVariant';

interface AuthCashflowGraphProps {
  variant?: AuthVisualVariant;
  compact?: boolean;
}

export const AuthCashflowGraph: React.FC<AuthCashflowGraphProps> = ({
  variant = 'light',
  compact = false,
}) => {
  const dark = variant === 'dark';
  const reduceMotion = useReducedMotion();
  const [hovered, setHovered] = useState(false);
  const [tick, setTick] = useState(0);

  const data = useMemo(() => {
    if (reduceMotion || tick === 0) return authCashflowData;
    const bump = Math.sin(tick * 0.4) * 2;
    return authCashflowData.map((row, i) =>
      i === authCashflowData.length - 1
        ? { ...row, revenue: Math.round(row.revenue + bump), forecast: Math.round(row.forecast + bump * 0.6) }
        : row
    );
  }, [reduceMotion, tick]);

  React.useEffect(() => {
    if (reduceMotion) return undefined;
    const id = window.setInterval(() => setTick((t) => t + 1), 2800);
    return () => window.clearInterval(id);
  }, [reduceMotion]);

  const latest = data[data.length - 1]!;
  const net = latest.revenue - latest.expenses;

  return (
    <motion.div
      className="relative"
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
    >
      <motion.div
        className="pointer-events-none absolute inset-x-4 bottom-8 h-16 rounded-full bg-blue-500/10 blur-2xl"
        animate={{ opacity: hovered && !reduceMotion ? 0.55 : 0.2 }}
        transition={{ duration: 0.5 }}
        aria-hidden
      />

      <motion.div
        className={cn('flex items-center justify-between gap-3', compact ? 'mb-2' : 'mb-3')}
        initial={reduceMotion ? false : { opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15, duration: 0.4 }}
      >
        <motion.div
          className={cn(
            'flex items-center gap-1.5 rounded-full border px-2 py-0.5',
            dark ? 'border-white/10 bg-white/5' : 'border-slate-200 bg-slate-50'
          )}
        >
          <motion.span
            className="h-1.5 w-1.5 rounded-full bg-emerald-500"
            animate={reduceMotion ? {} : { opacity: [1, 0.45, 1] }}
            transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut' }}
            aria-hidden
          />
          <span
            className={cn(
              'text-[10px] font-medium uppercase tracking-wide',
              dark ? 'text-slate-400' : 'text-slate-500'
            )}
          >
            Live
          </span>
        </motion.div>
        <motion.div
          key={net}
          initial={reduceMotion ? false : { opacity: 0.6 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.35 }}
          className="text-right"
        >
          <p className="text-[11px] text-slate-400">Net · {latest.month}</p>
          <p className={cn('text-sm font-semibold tabular-nums', dark ? 'text-emerald-400' : 'text-emerald-700')}>
            ₹{net}L
          </p>
        </motion.div>
      </motion.div>

      <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Cashflow</p>
      <p className={cn('text-sm font-semibold', dark ? 'text-slate-100' : 'text-slate-900')}>
        Revenue vs expenses
      </p>

      <motion.div
        className={cn('mt-2 w-full', compact ? 'h-[84px]' : 'mt-3 h-[132px]')}
        animate={reduceMotion ? {} : { opacity: hovered ? 1 : 0.92 }}
        transition={{ duration: 0.35 }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 4, right: 4, left: -22, bottom: 0 }}>
            <defs>
              <linearGradient id={`authRevenueFill-${variant}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#2563eb" stopOpacity={hovered ? 0.2 : 0.14} />
                <stop offset="100%" stopColor="#2563eb" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke={dark ? '#334155' : '#f1f5f9'} strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 10, fill: '#94a3b8' }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis hide domain={['dataMin - 20', 'dataMax + 30']} />
            <Tooltip
              contentStyle={{
                fontSize: 11,
                borderRadius: 10,
                border: '1px solid #e2e8f0',
                boxShadow: '0 4px 12px rgb(0 0 0 / 0.06)',
              }}
              formatter={(v: number) => [`₹${v}L`, '']}
            />
            <Area
              type="monotone"
              dataKey="revenue"
              stroke="#2563eb"
              strokeWidth={hovered ? 2.25 : 2}
              fill={`url(#authRevenueFill-${variant})`}
              isAnimationActive={!reduceMotion}
              animationDuration={900}
              dot={false}
              activeDot={
                reduceMotion
                  ? false
                  : { r: 4, fill: '#2563eb', stroke: '#fff', strokeWidth: 2 }
              }
            />
            <Area
              type="monotone"
              dataKey="expenses"
              stroke="#cbd5e1"
              strokeWidth={1.5}
              fill="transparent"
              isAnimationActive={!reduceMotion}
              animationDuration={800}
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="forecast"
              stroke="#0d9488"
              strokeWidth={1.5}
              strokeDasharray="4 4"
              dot={false}
              isAnimationActive={!reduceMotion}
              animationDuration={1000}
            />
          </AreaChart>
        </ResponsiveContainer>
      </motion.div>

      <motion.div
        className={cn('mt-2 flex flex-wrap gap-3 text-[11px]', dark ? 'text-slate-400' : 'text-slate-500')}
        variants={{ hidden: { opacity: 0 }, visible: { opacity: 1 } }}
      >
        <span className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-blue-600" aria-hidden />
          Revenue
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-slate-300" aria-hidden />
          Expenses
        </span>
        <span className="flex items-center gap-1.5">
          <span
            className="h-2 w-2 rounded-full border border-dashed border-teal-600"
            aria-hidden
          />
          Forecast
        </span>
      </motion.div>
    </motion.div>
  );
};
